import React, { useContext, useEffect } from 'react';
import posthog from 'posthog-js';
import { I18nContext } from '../context/translations/TranslationsContext.context';
import ProjectConfig from '../constants';

/**
 * Lo que ve quien entra con un navegador que no admite lo que Tailwind 4 necesita.
 *
 * 🔴 TODOS los estilos van en línea, a propósito. Esta pantalla existe precisamente porque el
 * CSS de la aplicación no se va a aplicar: escribirla con las mismas utilidades que están rotas
 * la dejaría tan ilegible como lo que viene a explicar. Por eso tampoco usa ningún componente
 * de `ui/` — todos son Tailwind por dentro.
 *
 * Los estilos en línea están entre las cosas que este repositorio evita, y esta es la excepción
 * que lo justifica: es la única pantalla cuyo requisito es funcionar cuando la hoja de estilos
 * no funciona.
 */

/** Se deja continuar: es decisión de quien entra, y avisada. Ver la tarea del aviso. */
interface AvisoNavegadorProps {
	onContinuar: () => void;
}

const contenedor: React.CSSProperties = {
	minHeight: '100vh',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: '24px',
	// Sin webfont: las de Google puede que tampoco carguen, y aquí no se puede depender de eso.
	fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
	backgroundColor: '#F5F5F5',
	color: '#1A1A1A',
	lineHeight: 1.55
};

const tarjeta: React.CSSProperties = {
	maxWidth: '560px',
	width: '100%',
	backgroundColor: '#FFFFFF',
	border: '1px solid #EDEDED',
	borderRadius: '12px',
	padding: '32px'
};

const titulo: React.CSSProperties = {
	margin: '0 0 16px',
	fontSize: '22px',
	fontWeight: 700,
	color: ProjectConfig.ARCA_COLORS.GREEN_800
};

const parrafo: React.CSSProperties = { margin: '0 0 16px', fontSize: '16px' };

const aviso: React.CSSProperties = {
	margin: '0 0 24px',
	padding: '12px 16px',
	backgroundColor: '#F5F5F5',
	borderRadius: '8px',
	fontSize: '14px'
};

const boton: React.CSSProperties = {
	// 44 px de alto, el mismo mínimo pulsable que el resto del producto.
	minHeight: '44px',
	padding: '0 20px',
	fontSize: '16px',
	fontWeight: 600,
	color: '#FFFFFF',
	backgroundColor: ProjectConfig.ARCA_COLORS.GREEN_800,
	border: 'none',
	borderRadius: '999px',
	cursor: 'pointer'
};

const pie: React.CSSProperties = { margin: '24px 0 0', fontSize: '14px', color: '#5A5A5A' };

const AvisoNavegador: React.FC<AvisoNavegadorProps> = ({ onContinuar }) => {
	const {
		state: { translate }
	} = useContext(I18nContext);

	useEffect(() => {
		// Para saber a cuánta gente le pasa de verdad: la estimación previa era 7 personas de
		// 1.143 en 90 días, y conviene contrastarla con lo que ocurra.
		//
		// Se llama a `posthog` directamente y no por el hook porque el proveedor NO se monta
		// cuando la analítica está desactivada (ver `PostHogWrapper`), y entonces el hook no
		// existe.
		//
		// Se aplaza un turno del bucle de eventos por una razón que ya no aplica: `posthog.init`
		// vivía en el efecto de `PostHogWrapper`, nuestro ANCESTRO, y React vacía los efectos de
		// hijo a padre, así que aquí PostHog aún no estaba iniciado y `capture` se iba sin encolar
		// nada (posthog-js 1.358.1). Desde el 01/10/26 el `init` va al cargar el módulo
		// (`providers/PostHogProvider.tsx`), antes de pintar nada; el aplazamiento no estorba.
		const id = setTimeout(() => {
			posthog.capture('browser_unsupported_shown', {
				userAgent: navigator.userAgent,
				idioma: navigator.language
			});
		}, 0);
		return () => clearTimeout(id);
	}, []);

	return (
		<div style={contenedor}>
			<div style={tarjeta} role="alert">
				<h1 style={titulo}>{translate('browserWarning.title')}</h1>
				<p style={parrafo}>{translate('browserWarning.body')}</p>
				<p style={parrafo}>{translate('browserWarning.howTo')}</p>
				<p style={aviso}>{translate('browserWarning.riskNotice')}</p>

				<button type="button" style={boton} onClick={onContinuar}>
					{translate('browserWarning.continue')}
				</button>

				<p style={pie}>
					{translate('browserWarning.contact')}{' '}
					<a
						href={`mailto:${ProjectConfig.ARCA_CONTACT.EMAIL}`}
						style={{ color: ProjectConfig.ARCA_COLORS.GREEN_800 }}
					>
						{ProjectConfig.ARCA_CONTACT.EMAIL}
					</a>
				</p>
			</div>
		</div>
	);
};

export default AvisoNavegador;
